import React, { FC } from 'react';
import { useTranslation } from '@blocs.i18n';
import Icon from '@minecraft.icon';
import {
  BasicContainer,
  BodyTextLarge,
  BodyTextSmall,
  ButtonAsWrapper,
  DetailText,
  FlexCol,
  FlexRow,
} from '@minecraft.atoms';
import { CARD_TYPES, PAYMENT_TYPES } from '../enums/PaymentMethods';
import { APPLE_PAY, PAYPAL } from '../constants';

export type CreditCardProps = {
  cardType: string;
  paymentType?: string;
  lastFour: string;
  onSelect?: () => void;
  isSelected?: boolean;
  isPrimary?: boolean;
  isBackup?: boolean;
  hideArrow?: boolean;
};

export const isPaymentTypeApplePay = (paymentType: string) => {
  return [PAYMENT_TYPES.APPLE_PAY, PAYMENT_TYPES.APPLE_PAY_MERCHANT_TOKEN].includes(paymentType as PAYMENT_TYPES);
};

export const displayCardType = (cardType: string, paymentType: string) => {
  const isApplePay = isPaymentTypeApplePay(paymentType);
  const isPayPal = paymentType === PAYMENT_TYPES.PAYPAL;

  if (isApplePay) return APPLE_PAY;
  if (isPayPal) return PAYPAL;

  return cardType;
};

export const displayCardIcon = (cardType: string, paymentType: string) => {
  const isApplePay = isPaymentTypeApplePay(paymentType);
  const isPayPal = paymentType === PAYMENT_TYPES.PAYPAL;

  if (isApplePay) return CARD_TYPES.ApplePay;
  if (isPayPal) return CARD_TYPES.PayPal;

  return CARD_TYPES[cardType];
};

const CardStatus: FC = ({ children }) => (
  <BasicContainer padding={1} borderRadius="sm" bgColor="creditCardPriorityTag" borderPlacement="all">
    <DetailText data-qa-id="payment-primary-tag" as="p">
      {children}
    </DetailText>
  </BasicContainer>
);

export const CreditCard: FC<CreditCardProps> = ({
  cardType,
  paymentType,
  lastFour,
  onSelect,
  isSelected = false,
  isPrimary = false,
  isBackup = false,
  hideArrow = false,
}) => {
  const { t } = useTranslation();
  const cardIcon = displayCardIcon(cardType, paymentType);

  return (
    <FlexCol
      w="full"
      minW="full"
      bgColor={isSelected ? 'card' : 'transparent'}
      borderRadius="md"
      customBorders={{
        top: { borderColor: 'default', borderSize: 0.06, borderStyle: 'solid' },
        bottom: { borderColor: 'default', borderSize: 0.06, borderStyle: 'solid' },
        left: {
          borderColor: isSelected ? 'select' : 'default',
          borderSize: isSelected ? 0.4 : 0.06,
          borderStyle: 'solid',
        },
        right: { borderColor: 'default', borderSize: 0.06, borderStyle: 'solid' },
      }}
    >
      <ButtonAsWrapper
        className="cn_w-full cn_relative"
        onClick={onSelect}
        aria-label={
          paymentType === PAYMENT_TYPES.PAYPAL ? PAYPAL : t('talent:billing.card.select', { cardType, lastFour })
        }
        unsetSizing
      >
        <FlexCol wrap="nowrap">
          {(isPrimary || isBackup) && (
            <BasicContainer className="cn_absolute cn_uppercase" top={0.5} right={0.5}>
              <FlexRow justifyContent="flex-end" gap="1">
                {isPrimary && <CardStatus>{t('common:billing.card.primary')}</CardStatus>}
                {isBackup && <CardStatus>{t('common:billing.card.backup')}</CardStatus>}
              </FlexRow>
            </BasicContainer>
          )}
          <BasicContainer w="full" minW="full" my="3" padding={2}>
            <FlexRow gap="4" w="full" alignItems="center" wrap="nowrap">
              {cardIcon && <Icon height={3.4} name={cardIcon} />}
              <FlexCol>
                <BodyTextLarge fontWeight="semibold">{displayCardType(cardType, paymentType)}</BodyTextLarge>
                {lastFour && <BodyTextSmall noWrap>{t('common:billing.card.name', { lastFour })}</BodyTextSmall>}
              </FlexCol>
              {!hideArrow && <Icon name="chevron-right-thick" themeColor="active" />}
            </FlexRow>
          </BasicContainer>
        </FlexCol>
      </ButtonAsWrapper>
    </FlexCol>
  );
};
